import { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import {useSessionContext} from "../../../../context/SessionContext";
import {PARAMETER_RESTORE_FORM_DATA} from "../../../../pages/config/settings";

/**
 * Sets a default value for a form field based on user settings or resets it based on user input changes to dependencies.
 *
 * @param fieldName The name of the form field.
 * @param defaultValue The default value to set when dependencies change due to user input.
 * @param valueFromUserSettings (Optional) Value from user settings to set as default on initial render.
 * @param dependencyFields (Optional) Array of field names to trigger the reset or set default when manually changed.
 */
export const useSetDefaultValue = <T,>(
    fieldName: string,
    defaultValue: T,
    valueFromUserSettings?: T,
    dependencyFields: string[] = []
) => {
    const session = useSessionContext();

    // Check if restoreFormData is set to "true" in session storage
    const shouldRestoreFormData = session.getFromSession(PARAMETER_RESTORE_FORM_DATA) === "true";
    // Store the initial value in a ref to prevent re-renders when it changes
    const shouldRestoreFormDataRef = useRef(shouldRestoreFormData);

    const { setValue, getValues, formState, watch } = useFormContext();
    const [dependencyValues, setDependencyValues] = useState(() =>
        dependencyFields.length > 0 ? getValues(dependencyFields) : []);

    const prevDependencyValues = useRef(dependencyValues);
    const hasInitialSetupCompleted = useRef(false);
    const [manualDependencyChangeDetected, setManualDependencyChangeDetected] = useState(false);
    const currentValue = getValues(fieldName);

    useEffect(() => {
        if (!hasInitialSetupCompleted.current) {
            // If restoreFormData is true, don't set any default values
            if (!shouldRestoreFormDataRef.current) {
                if (valueFromUserSettings !== undefined) {
                    setValue(fieldName, valueFromUserSettings);
                } else if (!currentValue && defaultValue !== undefined) {
                    setValue(fieldName, defaultValue);
                }
            }
            hasInitialSetupCompleted.current = true;
        }
    }, [valueFromUserSettings, defaultValue, currentValue, setValue, fieldName]);

    // Effect to track dependency values
    useEffect(() => {
        if (dependencyFields.length > 0) {
            const watchedDependencies = watch(dependencyFields);

            // Only update if the watched dependencies have actually changed
            if (JSON.stringify(watchedDependencies) !== JSON.stringify(dependencyValues)) {
                if (Array.isArray(watchedDependencies)) {
                    setDependencyValues(watchedDependencies);
                } else if (typeof watchedDependencies === 'object' && watchedDependencies !== null) {
                    const valuesArray = dependencyFields.map((field) => watchedDependencies[field]);
                    setDependencyValues(valuesArray);
                } else {
                    setDependencyValues([]);
                }
            }
        }
    }, [dependencyFields, watch, dependencyValues]);

    // Effect to handle dependency changes
    useEffect(() => {
        const dependenciesChanged = dependencyValues.length > 0
            && dependencyValues.some((value, index) => value !== prevDependencyValues.current[index]);

        // Reset due to programmatic dependency changes
        if (dependenciesChanged && !manualDependencyChangeDetected) {
            if (valueFromUserSettings !== undefined) {
                setValue(fieldName, valueFromUserSettings);
            } else {
                setValue(fieldName, defaultValue);
            }
        }

        // Reset due to manual dependency changes
        if (dependenciesChanged && manualDependencyChangeDetected) {
            setValue(fieldName, defaultValue);
            setManualDependencyChangeDetected(false);
        }

        // Update previous dependency values for the next render
        prevDependencyValues.current = dependencyValues;
    }, [dependencyValues, manualDependencyChangeDetected, setValue, fieldName, defaultValue, valueFromUserSettings]);

    // Detect manual changes to dependencies
    useEffect(() => {
        if (dependencyFields.length > 0) {
            const anyDependencyManuallyChanged = dependencyFields.some(
                (depField) => formState.dirtyFields[depField]
            );
            if (anyDependencyManuallyChanged) {
                setManualDependencyChangeDetected(true);
            }
        }
    }, [formState.dirtyFields, dependencyFields]);
};
